import { Box, Flex, Icon, Text, Textarea } from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HiX } from "react-icons/hi";

import { useToast } from "../../../../components";
import ShareableUserSelect, {
  ShareableUser,
} from "../../../../components/ShareableUserSelect/ShareableUserSelect";
import Modal from "../../../components/Modal/Modal";
import {
  CustomInsightsReportDocument,
  CustomInsightsReportFragment,
  useAnalyticsReportShareableUsersLazyQuery,
  useShareCustomInsightsReportMutation,
  useUnshareCustomInsightsReportMutation,
} from "../../../graphql";

type CustomInsightsReportShareModalProps = {
  reportId: string;
  creatorId: string;
  currentUserId: string;
  shares: CustomInsightsReportFragment["shares"];
  isOpen: boolean;
  onClose(): void;
};

export const CustomInsightsReportShareModal: React.FC<
  CustomInsightsReportShareModalProps
> = ({ reportId, creatorId, currentUserId, shares, isOpen, onClose }) => {
  const [selectedUsers, setSelectedUsers] = useState<ShareableUser[]>([]);
  const [message, setMessage] = useState<string>("");
  const toast = useToast();
  const [getShareData, { loading, data }] =
    useAnalyticsReportShareableUsersLazyQuery();

  const [shareReport] = useShareCustomInsightsReportMutation({
    onError: (err) => {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
      });
    },
    onCompleted() {
      toast({
        title: "Report shared",
        description:
          "Recipients will receive an email with a link to view the report.",
        status: "success",
      });
      closeHandler();
    },
  });

  const [unshareReport] = useUnshareCustomInsightsReportMutation({
    refetchQueries: [
      { query: CustomInsightsReportDocument, variables: { id: reportId } },
    ],
    onCompleted() {
      toast({
        title: "Share removed",
        status: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Failed to remove share",
        description: err.message,
        status: "error",
      });
    },
  });

  const handleUnshare = useCallback(
    (shareId: string) => {
      unshareReport({ variables: { shareId } });
    },
    [unshareReport]
  );

  const shareHandler = useCallback(() => {
    shareReport({
      variables: {
        reportId,
        shareToUserIds: selectedUsers.map((u) => u.id),
        message: message || undefined,
      },
    });
  }, [reportId, message, selectedUsers, shareReport]);

  const closeHandler = (): void => {
    setSelectedUsers([]);
    setMessage("");
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      getShareData();
    }
  }, [isOpen, getShareData]);

  const excludedUserIds = useMemo(() => {
    const ids = new Set<string>([creatorId]);
    shares.forEach((share) => {
      if (share.sharedTo?.id) {
        ids.add(share.sharedTo.id);
      }
    });
    return ids;
  }, [creatorId, shares]);

  const shareableUsers = (data?.currentUser?.organization.users || [])
    .filter((user) => !excludedUserIds.has(user.id))
    .map((user) => ({
      id: user.id,
      fullName: user.fullName,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      profilePicUrl: user.profilePicUrl,
    }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeHandler}
      isLoading={loading}
      headerText="Share report"
      completeButtonText="Share"
      onComplete={shareHandler}
      completeButtonDisabled={selectedUsers.length === 0}
      hasHeaderBorder
      bodyProps={{ px: "8", pb: "10" }}
    >
      <ShareableUserSelect
        shareableUsers={shareableUsers}
        selectedUsers={selectedUsers}
        onSelection={(users: ShareableUser[]) => setSelectedUsers(users)}
      />
      {selectedUsers.length > 0 && (
        <Textarea
          data-testid="share-message"
          mt="5"
          height="150px"
          placeholder="Add a message (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      )}
      {selectedUsers.length === 0 && shares.length > 0 && (
        <Box mt="5">
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb="2">
            Already shared with
          </Text>
          <Flex flexDir="column" gap={1}>
            {shares.map((share) => {
              const canUnshare =
                currentUserId === creatorId ||
                currentUserId === share.sharedBy?.id;
              return (
                <Flex key={share.id} alignItems="center" gap={2}>
                  <Text fontSize="sm" flex={1}>
                    {share.sharedTo?.fullName ??
                      share.sharedTo?.email ??
                      "User"}
                  </Text>
                  {canUnshare && (
                    <Icon
                      as={HiX}
                      boxSize={3}
                      color="gray.400"
                      cursor="pointer"
                      _hover={{ color: "gray.600" }}
                      onClick={() => handleUnshare(share.id)}
                      data-testid={`unshare-${share.id}`}
                    />
                  )}
                </Flex>
              );
            })}
          </Flex>
        </Box>
      )}
    </Modal>
  );
};
